/**
 * Customer Interaction Logger Service
 * Logs user interactions with phone/WhatsApp buttons to Directus customer_Interaction table
 */

import { directusOperations } from './directus';
import { getWIBTimeForDirectus, formatForIndonesianUser } from './timeUtils';

export interface InteractionLogData {
  customerId: string; // No_pelanggan (customer ID)
  customerName?: string; // For display purposes
  phoneNumber: string; // The phone number that was called/messaged
  interactionType: 'whatsapp' | 'phonecall'; // Interaction type as specified
  notes?: string; // Optional additional notes
  customerTambakId?: number; // Optional reference to specific tambak
  visitId?: number; // Optional reference to current visit
}

/**
 * Log a customer interaction when user clicks WhatsApp or Call button
 * FIXED: Creates pending interactions that user needs to complete later
 */
export const logCustomerInteraction = async (data: InteractionLogData): Promise<void> => {
  try {
    // Prepare interaction data for Directus
    const interactionData = {
      No_pelanggan: data.customerId,
      interaction_type: data.interactionType, // 'whatsapp' or 'phonecall'
      keterangan: generateInteractionNotes(data),
      priority: 'low' as const, // Default priority for button interactions
      customer_tambak_id: data.customerTambakId || null,
      visit_id: data.visitId || null,
      date_created: getWIBTimeForDirectus(),
      // FIXED: Create as pending task - user needs to complete the contact action
      done: null // Leave as pending for user to mark complete after contacting customer
    };

    // Log the interaction to Directus
    await directusOperations.createCustomerInteraction(interactionData);
    
    // Silent success - interaction logged successfully
    console.log(`✅ Logged ${data.interactionType} interaction as pending (done=null)`);
  } catch (error) {
    console.error(`❌ Failed to log ${data.interactionType} interaction:`, error);
    // Don't throw error to avoid blocking user action
    // Just log for monitoring purposes
  }
};

/**
 * Generate descriptive notes for the interaction
 * FIXED: Updated wording to reflect planned actions that need completion
 */
const generateInteractionNotes = (data: InteractionLogData): string => {
  const customerName = data.customerName || 'Customer';
  const phoneNumber = data.phoneNumber;
  const timestamp = formatForIndonesianUser(new Date());

  let baseMessage = '';
  
  if (data.interactionType === 'whatsapp') {
    baseMessage = `Plan to contact ${customerName} via WhatsApp (${phoneNumber}) - scheduled ${timestamp}`;
  } else if (data.interactionType === 'phonecall') {
    baseMessage = `Plan to call ${customerName} at ${phoneNumber} - scheduled ${timestamp}`;
  }

  // Add custom notes if provided
  if (data.notes) {
    baseMessage += `. Additional notes: ${data.notes}`;
  }

  // Add source context
  baseMessage += '. Initiated from mobile app interface.';

  return baseMessage;
};

/**
 * Log WhatsApp interaction
 */
export const logWhatsAppInteraction = async (
  customerId: string,
  phoneNumber: string,
  customerName?: string,
  customerTambakId?: number,
  visitId?: number,
  notes?: string
): Promise<void> => {
  await logCustomerInteraction({
    customerId,
    phoneNumber,
    customerName,
    interactionType: 'whatsapp',
    customerTambakId,
    visitId,
    notes
  });
};

/**
 * Log phone call interaction
 */
export const logPhoneCallInteraction = async (
  customerId: string,
  phoneNumber: string,
  customerName?: string,
  customerTambakId?: number,
  visitId?: number,
  notes?: string
): Promise<void> => {
  await logCustomerInteraction({
    customerId,
    phoneNumber,
    customerName,
    interactionType: 'phonecall',
    customerTambakId,
    visitId,
    notes
  });
};

/**
 * Log visit interaction when a visit is started
 * FIXED: Creates pending visit interaction that gets completed when visit finishes
 */
export const logVisitInteraction = async (
  customerId: string,
  customerName?: string,
  customerTambakId?: number,
  visitId?: number,
  notes?: string
): Promise<void> => {
  try {
    const interactionData = {
      No_pelanggan: customerId,
      interaction_type: 'Visit/Meeting', // Standard interaction type for visits
      keterangan: generateVisitInteractionNotes(customerId, customerName, customerTambakId, notes),
      priority: 'medium' as const, // Visits are higher priority than phone/WhatsApp
      customer_tambak_id: customerTambakId || null,
      visit_id: visitId || null,
      date_created: getWIBTimeForDirectus(),
      // FIXED: Visit starts as pending, gets marked done when visit actually completes
      done: null // Visit will be marked complete when the actual visit finishes
    };

    await directusOperations.createCustomerInteraction(interactionData);
    console.log('✅ Logged visit interaction as pending (done=null)');
  } catch (error) {
    console.error('❌ Failed to log visit interaction:', error);
  }
};

/**
 * Generate descriptive notes for visit interactions
 */
const generateVisitInteractionNotes = (
  customerId: string,
  customerName?: string,
  customerTambakId?: number,
  notes?: string
): string => {
  const customer = customerName || 'Customer';
  const timestamp = formatForIndonesianUser(new Date());
  
  let baseMessage = `Planned visit to ${customer} (${customerId}) scheduled for ${timestamp}`;
  
  if (customerTambakId) {
    baseMessage += `. Tambak ID: ${customerTambakId}`;
  }
  
  if (notes) {
    baseMessage += `. Visit purpose: ${notes}`;
  }
  
  baseMessage += '. Visit planned from mobile app interface.';
  
  return baseMessage;
};

/**
 * Utility to extract customer data from Customer object for logging
 */
export const extractCustomerDataForLogging = (customer: Record<string, unknown>): {
  customerId: string;
  customerName: string;
  customerTambakId?: number;
} => {
  return {
    customerId: customer.idcust || customer.No_pelanggan || customer.id,
    customerName: customer.name || customer.NamaPelangganTambak || customer.Nama_Pelanggan || 'Unknown Customer',
    customerTambakId: customer.tambak_id || customer.customer_tambak_id || undefined
  };
};